/**
 * @file Shared prev/next pagination bar with optional page-size select.
 */

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  PAGE_SIZE_OPTIONS,
  currentPage,
  pageCount,
  offsetForPage,
} from '../utils/pagination';

/**
 * Shared prev/next pagination bar.
 *
 * @param {object} props
 * @param {number} props.total
 * @param {number} props.limit
 * @param {number} props.offset
 * @param {(next: { limit: number, offset: number }) => void} props.onChange
 * @param {boolean} [props.showPageSize=true]
 * @param {number[]} [props.pageSizeOptions]
 */
export default function PaginationControls({
  total = 0,
  limit = 50,
  offset = 0,
  onChange,
  showPageSize = true,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}) {
  const tot = Math.max(0, Number(total) || 0);
  const lim = Math.max(1, Number(limit) || 1);
  const off = Math.max(0, Number(offset) || 0);
  const pages = pageCount(tot, lim);
  const page = currentPage(off, lim);
  const from = tot === 0 ? 0 : off + 1;
  const to = tot === 0 ? 0 : Math.min(off + lim, tot);

  if (tot === 0) return null;

  const goPrev = () => {
    if (page <= 1) return;
    onChange?.({ limit: lim, offset: offsetForPage(page - 1, lim) });
  };

  const goNext = () => {
    if (page >= pages) return;
    onChange?.({ limit: lim, offset: offsetForPage(page + 1, lim) });
  };

  const onPageSize = (e) => {
    const nextLimit = parseInt(e.target.value, 10) || lim;
    onChange?.({ limit: nextLimit, offset: 0 });
  };

  return (
    <div className="pagination-bar" role="navigation" aria-label="Pagination">
      <span className="pagination-summary">
        {from}–{to} of {tot}
      </span>

      <div className="pagination-controls">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={goPrev}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} aria-hidden />
          Prev
        </button>
        <span className="pagination-page" aria-live="polite">
          Page {page} of {pages}
        </span>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={goNext}
          disabled={page >= pages}
          aria-label="Next page"
        >
          Next
          <ChevronRight size={16} aria-hidden />
        </button>
      </div>

      {showPageSize && (
        <label className="pagination-page-size">
          <span className="sr-only">Items per page</span>
          <select value={lim} onChange={onPageSize} aria-label="Items per page">
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
